<template>
  <div class="cpu-module" :class="{ collapsed: isCollapsed }">
    <div class="sci-fi-title" @click="toggleCollapse"
      :title="isCollapsed ? langMap.dashboard_expandModule[lang] : langMap.dashboard_collapseModule[lang]">
      <div class="title-bg"></div>
      <h3>
        <Icon type="ios-pulse" />CPU
        <span v-if="cpuData && cpuData.totalCores" class="cpu-overview">
          <span class="overview-item">
            <span class="overview-label">{{ langMap.dashboard_totalUsage[lang] }}:</span>
            <span :class="['overview-value', getUsageClass(cpuData.totalUsage)]">{{ cpuData.totalUsage }}%</span>
          </span>
          <span class="overview-divider">|</span>
          <span class="overview-item">
            <span class="overview-label">{{ langMap.dashboard_totalCores[lang] }}:</span>
            <span class="overview-value">{{ cpuData.totalCores }}</span>
          </span>
        </span>
        <Icon :type="isCollapsed ? 'ios-arrow-down' : 'ios-arrow-up'" class="collapse-icon" />
      </h3>
      <div class="title-decoration"></div>
    </div>

    <div class="cpu-grid" v-show="!isCollapsed">
      <!-- 无数据提示 -->
      <div v-if="cpuCoreList.length === 0" class="no-data-state">
        <div class="hologram-container">
          <div class="hologram-ring ring-1"></div>
          <div class="hologram-ring ring-2"></div>
          <div class="hologram-ring ring-3"></div>
          <div class="hologram-icon">
            <Icon type="ios-pulse" />
          </div>
        </div>
        <div class="no-data-text">
          <div class="main-text">{{ langMap.dashboard_noData[lang] }}</div>
          <div class="sub-text">{{ langMap.dashboard_loading[lang] }}</div>
        </div>
      </div>
      <!-- CPU核心列表 -->
      <div v-for="(core, index) in cpuCoreList" :key="index" class="cpu-card">
        <div class="cpu-title">
          <span class="cpu-name-main">{{ core.name }}</span>
        </div>
        <div class="params-grid">
          <div v-if="core.coreUsage !== null" class="param-item">
            <div class="param-label">{{ langMap.dashboard_use[lang] }}</div>
            <div class="param-value">
              <span :class="getUsageClass(core.coreUsage)">{{ core.coreUsage }}</span>
              <span class="unit">%</span>
            </div>
          </div>
          <div class="param-item">
            <div class="param-label">{{ langMap.dashboard_temp[lang] }}</div>
            <div class="param-value">
              <span :class="getTempClass(core.coreTemp)">{{ core.coreTemp }}</span>
              <span class="unit">°C</span>
            </div>
          </div>
          <div class="param-item">
            <div class="param-label">{{ langMap.dashboard_freq[lang] }}</div>
            <div class="param-value">{{ core.realFreqMhz }}<span class="unit">MHz</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'
import collapsibleMixin from './collapsible-mixin'

export default {
  name: 'CpuModule',
  mixins: [collapsibleMixin],
  props: {
    cpuData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      langMap,
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language
    }),
    cpuCoreList() {
      if (!this.cpuData || !this.cpuData.cpuList || !this.cpuData.cpuList.length) {
        return [];
      }
      return this.cpuData.cpuList;
    }
  },
  methods: {
    getUsageClass(usage) {
      const u = parseFloat(usage)
      if (u >= 90) return 'high'
      if (u >= 70) return 'medium'
      return 'normal'
    },
    getTempClass(temp) {
      const t = parseFloat(temp)
      if (t >= 80) return 'high'
      if (t >= 60) return 'medium'
      return 'normal'
    }
  }
}
</script>

<style lang="less" scoped>
.cpu-module {
  /* 与其他模块一致的容器样式 */
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 10, 25, 0.95));
  border-radius: 8px;
  padding: 0 0 8px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(0, 240, 255, 0.1);
  border: 1px solid rgba(0, 240, 255, 0.3);
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  min-height: 0;

  &.collapsed { flex: 0 0 auto; }

  /* 标题条复用样式 */
  .sci-fi-title {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
    border-bottom: 2px solid rgba(0, 240, 255, 0.5);
    position: relative;
    z-index: 10;
    cursor: pointer;
    user-select: none;
    transition: all 0.3s ease;

    &:hover { background: linear-gradient(135deg, rgba(0, 50, 90, 0.9), rgba(0, 30, 50, 0.95)); border-bottom-color: rgba(0, 240, 255, 0.8); }

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #00f0ff;
      text-shadow: 0 0 15px rgba(0, 240, 255, 0.8);
      letter-spacing: 2px;
      display: flex;
      align-items: center;
      gap: 10px;
      
      .cpu-overview {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left: auto;
        margin-right: 8px;
        font-size: 11px;
        font-weight: 400;
        letter-spacing: 0.5px;
        
        .overview-item {
          display: flex;
          align-items: center;
          gap: 4px;
          
          .overview-label {
            color: rgba(255, 255, 255, 0.6);
            font-size: 10px;
          }
          
          .overview-value {
            font-family: 'Consolas', monospace;
            font-weight: 700;
            font-size: 12px;
            color: #fff;
            
            &.high { color: #ff4d4f; }
            &.medium { color: #faad14; }
            &.normal { color: #52c41a; }
          }
        }
        
        .overview-divider {
          color: rgba(0, 240, 255, 0.4);
          font-size: 10px;
        }
      }
      
      .collapse-icon {
        font-size: 18px;
        transition: transform 0.3s ease;
      }
    }
    .title-bg { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent); opacity: 0; transition: opacity 0.3s ease; }
    &:hover .title-bg { opacity: 1; }
    .title-decoration { position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%); width: 60%; height: 2px; background: linear-gradient(90deg, transparent, #00f0ff, transparent); box-shadow: 0 0 10px rgba(0, 240, 255, 0.8); }
  }

  /* 顶部扫描线与数据流效果 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 1) 50%, transparent);
    box-shadow: 0 0 20px rgba(0, 240, 255, 0.8);
    animation: scanline 2s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.1), transparent);
    animation: dataflow 3s ease-in-out infinite;
  }

  .cpu-grid {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0, 240, 255, 0.2);
    cursor: pointer;
    transition: all 0.3s ease;

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;

      .header-icon {
        font-size: 20px;
      }

      .header-title {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
      }
    }

    .header-right {
      .collapse-icon {
        font-size: 18px;
        color: #00f0ff;
        transition: transform 0.3s ease;
      }
    }
  }

  .cpu-grid {
    flex: 1;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 240, 255, 0.3);
      border-radius: 2px;
    }
    // 无数据状态
    .no-data-state {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 24px;
      padding: 48px 24px;
      min-height: 200px;

      .hologram-container {
        position: relative;
        width: 120px;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;

        .hologram-ring {
          position: absolute;
          border: 2px solid rgba(0, 240, 255, 0.4);
          border-radius: 50%;

          &.ring-1 {
            width: 120px;
            height: 120px;
            animation: pulse-ring 2s ease-in-out infinite;
          }

          &.ring-2 {
            width: 90px;
            height: 90px;
            animation: pulse-ring 2s ease-in-out 0.3s infinite;
          }

          &.ring-3 {
            width: 60px;
            height: 60px;
            animation: pulse-ring 2s ease-in-out 0.6s infinite;
          }
        }

        .hologram-icon {
          position: relative;
          z-index: 1;
          font-size: 48px;
          color: #00f0ff;
          text-shadow: 0 0 20px rgba(0, 240, 255, 0.8),
            0 0 40px rgba(0, 240, 255, 0.6),
            0 0 60px rgba(0, 240, 255, 0.4);
          animation: float 3s ease-in-out infinite;

          i {
            display: block;
          }
        }
      }

      .no-data-text {
        text-align: center;

        .main-text {
          font-size: 16px;
          font-weight: 600;
          color: #00f0ff;
          text-shadow: 0 0 10px rgba(0, 240, 255, 0.6);
          margin-bottom: 8px;
          letter-spacing: 1px;
        }

        .sub-text {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          animation: pulse-text 2s ease-in-out infinite;
        }
      }
    }
    }

  @keyframes pulse-ring {

    0%,
    100% {
      opacity: 0.4;
      transform: scale(1);
    }

    50% {
      opacity: 0.8;
      transform: scale(1.05);
    }
  }

  @keyframes float {

    0%,
    100% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(-10px);
    }
  }

    @keyframes pulse-text {

    0%,
    100% {
      opacity: 0.5;
    }

    50% {
      opacity: 1;
    }
  }

  .cpu-card {
    background: linear-gradient(135deg, rgba(0, 40, 80, 0.7), rgba(0, 20, 45, 0.9));
    border: 1px solid rgba(0, 240, 255, 0.4);
    border-radius: 8px;
    width: 100%;
    padding: 4px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(0, 240, 255, 0.2);

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: conic-gradient(transparent, rgba(0, 240, 255, 0.12), transparent 30%);
      animation: rotate 8s linear infinite;
      opacity: 0.6;
    }

    .cpu-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(0, 240, 255, 0.3);
      position: relative;
      z-index: 1;

      .cpu-name-main {
        font-size: 13px;
        font-weight: 600;
        color: #00f0ff;
        text-shadow: 0 0 12px rgba(0, 240, 255, 0.6);
        letter-spacing: 1px;
      }

      .cpu-name-sub {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.4);
        font-family: 'Consolas', monospace;
        margin-left: auto;
      }
    }

    .params-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      position: relative;
      z-index: 1;

      .param-item {
        background: linear-gradient(135deg, rgba(0, 240, 255, 0.12), rgba(0, 180, 255, 0.08));
        border: 1px solid rgba(0, 240, 255, 0.35);
        border-radius: 6px;
        padding: 4px;
        text-align: center;
        position: relative;
        overflow: hidden;
        flex: 0 0 31.333%;
        min-width: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(0, 240, 255, 0.15);

        .param-label {
          font-size: 11px;
          font-weight:bold;
          color: rgba(255, 255, 255, 0.65);
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          letter-spacing: 0.5px;
          width: 100%;
        }

        .param-value {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          font-family: 'Consolas', 'Monaco', monospace;
          white-space: nowrap;
          text-shadow: 0 0 15px rgba(255, 255, 255, 0.6), 0 0 30px rgba(0, 240, 255, 0.5);
          transition: all 0.3s ease;
          position: relative;
          display: inline-block;
          line-height: 1;
          width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;

          span.high { color: #ff4d4f; }
          span.medium { color: #faad14; }
          span.normal { color: #52c41a; }

          .unit {
            font-size: 10px;
            color: rgba(0, 240, 255, 1);
            margin-left: 2px;
            font-weight: 600;
            text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
          }
        }
      }
    }
  }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes scanline {
  0% { transform: translateY(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(500px); opacity: 0; }
}

@keyframes dataflow {
  0% { left: -100%; }
  100% { left: 100%; }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
